import { type File, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { type MouseEvent, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { useOpenChat } from '@/features/chat/use-open-chat';
import { SharePublishedSitePopover } from '@/features/published-sites/SharePublishedSitePopover';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { downloadFileWithFeedback } from '@/utils/file-io';

import { canShareSprite, spriteTypeOf } from './sprite-presentation';
import { deleteSpriteWithFeedback, type SpriteSummary } from './sprites.store';

interface SpriteActionsMenuProps {
  sprite: SpriteSummary;
  file?: File;
  onOpen: () => void;
  isMenuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  className?: string;
}

export const SpriteActionsMenu = observer(
  ({
    sprite,
    file,
    onOpen,
    isMenuOpen,
    onMenuOpenChange,
    className,
  }: SpriteActionsMenuProps) => {
    const { files: filesStore } = useStores();
    const { openChat, isOpening } = useOpenChat();
    const [isShareOpen, setIsShareOpen] = useState(false);
    const handingOffToShare = useRef(false);

    const canShare = canShareSprite(sprite);
    const canDownload =
      !!file && spriteTypeOf(sprite.content_type, sprite.name) !== 'canvas';
    const sourceChatId = sprite.source_chat_id;

    const trigger = (
      <DropdownMenu.Trigger asChild>
        <IconButton
          size="default"
          variant="default"
          icon={Icons.moreHorizontal}
          aria-label={`Actions for ${sprite.name}`}
          onClick={(e: MouseEvent) => e.stopPropagation()}
          // The row/card is itself an activation target; without this Enter/Space would both
          // operate this menu and bubble up to open the artifact.
          onKeyDown={(e) => e.stopPropagation()}
          className={cn(
            'opacity-0 transition-opacity duration-150',
            'touch:opacity-100',
            'group-hover:opacity-100 data-[state=open]:opacity-100',
            'focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100',
            'data-[state=open]:bg-surface-hover',
            isShareOpen && 'opacity-100 bg-surface-hover',
            className
          )}
        />
      </DropdownMenu.Trigger>
    );

    return (
      <DropdownMenu.Root
        open={isMenuOpen}
        onOpenChange={(open) => {
          if (open) setIsShareOpen(false);
          onMenuOpenChange(open);
        }}
      >
        {canShare ? (
          <SharePublishedSitePopover
            target={{ spriteId: sprite.id }}
            open={isShareOpen}
            onOpenChange={setIsShareOpen}
            anchorOnly
          >
            {trigger}
          </SharePublishedSitePopover>
        ) : (
          trigger
        )}
        <DropdownMenu.Content
          align="end"
          fit
          className="min-w-44"
          onClick={(e) => e.stopPropagation()}
          onCloseAutoFocus={(e) => {
            if (!handingOffToShare.current) return;
            e.preventDefault();
            handingOffToShare.current = false;
          }}
        >
          <DropdownMenu.Item onSelect={onOpen}>
            <Icons.gridPlus />
            Open
          </DropdownMenu.Item>
          {sourceChatId && (
            <DropdownMenu.Item
              disabled={isOpening}
              onSelect={() => {
                trackEvent('SPRITE_CHAT_OPENED', {
                  sprite_id: sprite.id,
                  chat_id: sourceChatId,
                });
                void openChat(sourceChatId);
              }}
            >
              <Icons.chat />
              Refine in chat
            </DropdownMenu.Item>
          )}
          {canDownload && (
            <DropdownMenu.Item
              onSelect={() =>
                void downloadFileWithFeedback(
                  filesStore,
                  file,
                  'SpriteActionsMenu'
                )
              }
            >
              <Icons.download />
              Download
            </DropdownMenu.Item>
          )}
          {canShare && (
            <DropdownMenu.Item
              onSelect={() => {
                handingOffToShare.current = true;
                setIsShareOpen(true);
              }}
            >
              <Icons.share />
              Share
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Separator />
          <DropdownMenu.ItemWithConfirm
            className="items-center"
            onConfirm={() =>
              void deleteSpriteWithFeedback(sprite, 'list', 'SpriteActionsMenu')
            }
            icon={Icons.trash}
            ariaLabel={`Delete artifact ${sprite.name}`}
          >
            Delete
          </DropdownMenu.ItemWithConfirm>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);
